import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, ChevronRight, Plus, User as UserIcon } from "lucide-react";
import { api } from "../api/client";
import { wsUrl } from "../lib/wsUrl";
import { format } from "date-fns";

const ChatView = ({ user, token }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [roomId, setRoomId] = useState(null);
  const [partnerName, setPartnerName] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    initChat();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const initChat = async () => {
    try {
      // Get partner ID (trainer for client, or first client for trainer)
      let partnerId = user.trainer_id;
      if (user.role === "trainer") {
        const clientsRes = await api("get", "/trainer/clients", null, token);
        if (clientsRes.data?.length > 0) {
          partnerId = clientsRes.data[0].id;
          setPartnerName(clientsRes.data[0].name);
        }
      } else {
        // Get trainer name
        const trainerRes = await api("get", "/auth/me", null, token);
        if (trainerRes.data?.trainer_id) {
          setPartnerName("Trener");
        }
      }

      if (!partnerId) {
        setLoading(false);
        return;
      }

      // Get chat room
      const roomRes = await api("get", `/chat/room/${partnerId}`, null, token);
      setRoomId(roomRes.data.room_id);
      setMessages(roomRes.data.messages || []);
      setLoading(false);

      // Setup WebSocket
      wsRef.current = new WebSocket(wsUrl(`/ws/chat/${roomRes.data.room_id}`, token));

      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);
        setMessages(prev => [...prev, message]);
      };
    } catch (err) {
      console.error("Chat init error:", err);
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !roomId) return;

    try {
      await api("post", `/chat/room/${roomId}/message`, { content: newMessage }, token);
      setNewMessage("");
    } catch (err) {
      toast.error("Nie udało się wysłać wiadomości");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) return <div className="loading"><MessageCircle className="spin" size={32} /></div>;

  if (!user.trainer_id && user.role === "client") {
    return (
      <>
        <section className="greeting">
          <h1>Czat z trenerem</h1>
        </section>
        <Card className="empty-card">
          <CardContent>
            <MessageCircle size={32} className="empty-icon" />
            <p>Połącz się z trenerem aby rozpocząć czat</p>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <div className="chat-view" data-testid="chat-view">
      <section className="chat-header-v2">
        <div className="chat-trainer-info">
          <div className="chat-avatar-gradient">
            <UserIcon size={24} color="#FFF" />
          </div>
          <div className="chat-trainer-details">
            <h2>{partnerName || 'Trener'}</h2>
            <div className="chat-status-row">
              <div className="chat-status-dot" />
              <span>Online</span>
            </div>
          </div>
        </div>
      </section>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <MessageCircle size={32} />
            <p>Brak wiadomości. Napisz pierwszą!</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const msgDate = new Date(msg.created_at).toDateString();
            const prevDate = idx > 0 ? new Date(messages[idx - 1].created_at).toDateString() : null;
            const showSeparator = idx === 0 || msgDate !== prevDate;
            const today = new Date().toDateString();
            const yesterday = new Date(Date.now() - 86400000).toDateString();
            const dateLabel = msgDate === today ? "Dzis" : msgDate === yesterday ? "Wczoraj" : format(new Date(msg.created_at), "d MMM yyyy");
            return (
              <div key={msg.id}>
                {showSeparator && (
                  <div className="chat-date-separator">
                    <span>{dateLabel}</span>
                  </div>
                )}
                <div className={`chat-message ${msg.sender_id === user.id ? 'own' : 'other'}`}>
                  <div className="message-content">
                    <span className="message-sender">{msg.sender_name}</span>
                    <p>{msg.content}</p>
                    <span className="message-time">
                      {format(new Date(msg.created_at), "HH:mm")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <button className="chat-attach-btn" title="Zalacz">
          <Plus size={20} />
        </button>
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Napisz wiadomość..."
          data-testid="chat-input"
        />
        <Button onClick={sendMessage} disabled={!newMessage.trim()} data-testid="send-message">
          <Send size={18} />
        </Button>
      </div>
    </div>
  );
};

export default ChatView;
